
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, FileText, Users, Bot } from "lucide-react";
import { PhaseAssignmentList } from "./PhaseAssignmentList";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { TaskAssignmentModal } from "@/components/tasks/TaskAssignmentModal";
import { useTaskBulkOperations } from "@/hooks/useTaskBulkOperations";

interface ProjectPRDPanelProps {
  project: any;
  phases?: any[];
  tasks?: any[];
  isInitialSetup?: boolean;
  onDataChange?: () => void;
}

interface Phase {
  id?: string;
  name: string;
  assignedUserId?: string;
}

interface User {
  id: string;
  role: string;
}

export const ProjectPRDPanel = ({ 
  project, 
  phases: existingPhases = [], 
  tasks: existingTasks = [],
  isInitialSetup = false,
  onDataChange 
}: ProjectPRDPanelProps) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { session } = useAuth();
  const { createTasksWithAssignments, isCreating } = useTaskBulkOperations();
  
  const [prd, setPrd] = useState(project?.prd || "");
  const [phases, setPhases] = useState<Phase[]>(
    existingPhases.length > 0 
      ? existingPhases.map(p => ({ id: p.id, name: p.name, assignedUserId: p.assigned_to }))
      : [
          { name: "Configuración inicial" },
          { name: "Backend" },
          { name: "Frontend" },
          { name: "Testing y QA" }
        ]
  );
  const [users, setUsers] = useState<User[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState<any[]>([]);
  const [showTaskAssignment, setShowTaskAssignment] = useState(false);
  const [taskAssignments, setTaskAssignments] = useState<Record<string, string>>({});

  useEffect(() => {
    if (project?.prd) {
      setPrd(project.prd);
    }
  }, [project?.prd]);

  useEffect(() => {
    if (existingPhases.length > 0) {
      setPhases(existingPhases.map(p => ({ 
        id: p.id, 
        name: p.name, 
        assignedUserId: p.assigned_to 
      })));
    }
  }, [existingPhases]);

  const handleSavePRD = async () => {
    if (!prd.trim()) {
      toast({
        title: "Error",
        description: "Please enter a PRD description",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('projects')
        .update({ prd })
        .eq('id', project.id);

      if (error) throw error;

      toast({
        title: "PRD Saved",
        description: "Project requirements have been saved successfully",
      });

      if (onDataChange) onDataChange();
    } catch (error: any) {
      console.error('Error saving PRD:', error);
      toast({
        title: "Error",
        description: "Failed to save PRD",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePhases = async () => {
    if (phases.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one phase",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create or update phases
      const phasesToUpsert = phases.map((phase, index) => ({
        id: phase.id,
        project_id: project.id,
        name: phase.name,
        order_index: index,
        status: 'active',
        description: `Phase ${index + 1}: ${phase.name}`
      }));

      // If we have existing phases, we need to handle updates differently
      if (existingPhases.length > 0) {
        // Update existing phases
        for (const phase of phasesToUpsert) {
          if (phase.id) {
            await supabase
              .from('project_phases')
              .update({
                name: phase.name,
                order_index: phase.order_index,
                description: phase.description
              })
              .eq('id', phase.id);
          } else {
            // Create new phase
            await supabase
              .from('project_phases')
              .insert({
                project_id: phase.project_id,
                name: phase.name,
                order_index: phase.order_index,
                status: phase.status,
                description: phase.description
              });
          }
        }
      } else {
        // Create all phases for the first time
        const { error: phasesError } = await supabase.functions.invoke('project-tracking-operations', {
          body: {
            action: "createMultiplePhases",
            projectId: project.id,
            organizationId: currentOrganization?.id,
            userId: session?.user.id,
            phases: phasesToUpsert
          }
        });

        if (phasesError) throw phasesError;
      }

      toast({
        title: "Phases Saved",
        description: "Project phases have been saved successfully",
      });

      if (onDataChange) onDataChange();
    } catch (error: any) {
      console.error('Error saving phases:', error);
      toast({
        title: "Error",
        description: "Failed to save phases",
        variant: "destructive",
      });
    }
  };

  const handleGenerateTasks = async () => {
    if (!prd.trim()) {
      toast({
        title: "Error",
        description: "Please enter a PRD description first",
        variant: "destructive",
      });
      return;
    }

    if (phases.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one phase",
        variant: "destructive",
      });
      return;
    }

    // First save PRD and phases
    await handleSavePRD();
    await handleSavePhases();

    setIsGenerating(true);
    try {
      // Mock AI task generation (you can replace this with actual AI call)
      const mockTasks = [
        {
          title: "Setup project environment",
          description: "Initialize project structure and dependencies",
          priority: "high",
          phase_id: phases[0].id || "temp-1"
        },
        {
          title: "Design database schema",
          description: "Create and implement database design",
          priority: "high",
          phase_id: phases[1]?.id || "temp-2"
        },
        {
          title: "Implement authentication",
          description: "Setup user authentication system",
          priority: "medium",
          phase_id: phases[1]?.id || "temp-2"
        },
        {
          title: "Create UI components",
          description: "Build reusable UI components",
          priority: "medium",
          phase_id: phases[2]?.id || "temp-3"
        },
        {
          title: "Integration testing",
          description: "Test all system integrations",
          priority: "high",
          phase_id: phases[3]?.id || "temp-4"
        }
      ];

      setGeneratedTasks(mockTasks);
      setShowTaskAssignment(true);

    } catch (error: any) {
      console.error('Error generating tasks:', error);
      toast({
        title: "Error",
        description: "Failed to generate tasks",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTaskAssignmentSave = async () => {
    try {
      await createTasksWithAssignments(generatedTasks, taskAssignments);
      setShowTaskAssignment(false);
      
      toast({
        title: "Tasks Created",
        description: "AI-generated tasks have been created successfully",
      });

      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error creating tasks:', error);
    }
  };

  const triggerAIAssistant = () => {
    const aiButton = document.querySelector('[data-ai-assistant-trigger]') as HTMLButtonElement;
    if (aiButton) {
      aiButton.click();
    } else {
      console.log('AI Assistant button not found');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold">
            {isInitialSetup ? "Project Setup" : "PRD & Phases Management"}
          </h2>
          <p className="text-muted-foreground">
            {isInitialSetup 
              ? "Define your project requirements and phases" 
              : "Edit project requirements and manage phases"
            }
          </p>
        </div>
      </div>

      {/* PRD Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Product Requirements Document (PRD)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="prd">Describe your project requirements</Label>
            <Textarea
              id="prd"
              placeholder="Example: I want a hotel booking platform with authentication, room management, calendar, and payment system..."
              value={prd}
              onChange={(e) => setPrd(e.target.value)}
              rows={6}
              className="mt-2"
            />
          </div>
          <Button 
            onClick={handleSavePRD} 
            disabled={isSaving || !prd.trim()}
            variant="outline"
            className="w-full"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save PRD
          </Button>
        </CardContent>
      </Card>

      {/* Phases Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Project Phases
            {existingPhases.length > 0 && (
              <Badge variant="outline" className="ml-2">
                {existingPhases.length} phases
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <PhaseAssignmentList 
            phases={phases}
            onPhasesChange={setPhases}
            users={users}
            onUsersChange={setUsers}
          />
          <Button 
            onClick={handleSavePhases}
            variant="outline"
            className="w-full"
          >
            Save Phases
          </Button>
        </CardContent>
      </Card>

      {/* Task Generation Section */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Generate Tasks</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create tasks automatically based on your PRD and phases
            </p>
          </div>
          
          <div className="grid gap-2 sm:grid-cols-2">
            <Button 
              onClick={handleGenerateTasks}
              disabled={isGenerating || !prd.trim() || phases.length === 0}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Tasks (Mock AI)
                </>
              )}
            </Button>
            
            <Button 
              onClick={triggerAIAssistant}
              disabled={!prd.trim() || phases.length === 0}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Bot className="h-4 w-4 mr-2" />
              AI Task Generator
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center">
            Use the mock generator for testing or the AI assistant for real task generation
          </p>
        </CardContent>
      </Card>

      {/* Task Assignment Modal */}
      <TaskAssignmentModal
        open={showTaskAssignment}
        onOpenChange={setShowTaskAssignment}
        tasks={generatedTasks}
        onAssignmentsChange={setTaskAssignments}
        onSave={handleTaskAssignmentSave}
      />
    </div>
  );
};
